import { useTheme } from '@/context/ThemeContext';

const benefits = [
  {
    icon: 'ri-eye-line',
    title: 'Operational Clarity',
    description:
      'Complete visibility into every process stage. Know exactly where each inquiry stands, what actions have been taken, and what comes next — without chasing updates.',
    metric: 'Full pipeline visibility',
    metricIcon: 'ri-bar-chart-2-line',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Revenue Protection',
    description:
      'Zero missed opportunities through systematic capture. Every inquiry is logged, every lead is followed up, and every conversion opportunity is pursued — automatically.',
    metric: 'Zero revenue leakage',
    metricIcon: 'ri-funds-line',
  },
  {
    icon: 'ri-expand-diagonal-line',
    title: 'Scalable Infrastructure',
    description:
      'Growth without operational strain or complexity. Your systems scale with your business — handling increased volume without requiring proportional increases in headcount.',
    metric: 'Scales without friction',
    metricIcon: 'ri-arrow-up-line',
  },
  {
    icon: 'ri-timer-line',
    title: 'Predictable Performance',
    description:
      'Consistent execution regardless of volume. Whether you receive 10 inquiries or 1,000, your systems respond with the same speed, accuracy, and professionalism.',
    metric: 'Consistent at any scale',
    metricIcon: 'ri-pulse-line',
  },
];

export default function Benefits() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="benefits" className={`py-28 px-6 md:px-10 transition-colors duration-500 ${
      isDark ? 'bg-[#0b1f3a]' : 'bg-[#e8f4f6]'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left: Image */}
          <div className="lg:w-[45%] shrink-0">
            <div className="relative rounded-2xl overflow-hidden">
              <div className="w-full h-[500px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/22ff58303cda7149bd91e0556651cf17.jpg"
                  alt="Operational Intelligence Visualization"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className={`absolute inset-0 ${
                isDark ? 'bg-gradient-to-t from-[#0b1f3a]' : 'bg-gradient-to-t from-[#e8f4f6]'
              } via-transparent to-transparent`}></div>

              {/* Floating stat card */}
              <div className={`absolute bottom-8 left-8 right-8 backdrop-blur-sm border rounded-xl p-5 ${
                isDark
                  ? 'bg-[#0b1f3a]/90 border-[#14b8c8]/20'
                  : 'bg-white/90 border-[#0d7a8a]/20'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-xs mb-1 ${isDark ? 'text-white/50' : 'text-slate-400'}`}>Systems Operating</div>
                    <div className={`font-bold text-2xl ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>24/7/365</div>
                  </div>
                  <div className="flex gap-1 items-end">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className={`w-1.5 rounded-full ${isDark ? 'bg-[#14b8c8]' : 'bg-[#0d7a8a]'}`} style={{ height: `${20 + i * 8}px` }}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Benefits */}
          <div className="flex-1">
            <span className={`text-xs font-medium tracking-widest uppercase mb-4 block ${
              isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
            }`}>
              WHY CORNERSTONE
            </span>
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 leading-tight ${
              isDark ? 'text-white' : 'text-[#0b1f3a]'
            }`}>
              The Results Speak<br />
              <span className={`font-thin ${isDark ? 'text-white/50' : 'text-[#0b1f3a]/50'}`}>For Themselves</span>
            </h2>
            <p className={`text-base leading-relaxed mb-12 max-w-md ${
              isDark ? 'text-white/50' : 'text-slate-500'
            }`}>
              When your operations run on structured intelligence, the outcomes are measurable, consistent, and compounding.
            </p>

            <div className="flex flex-col gap-8">
              {benefits.map((b) => (
                <div key={b.title} className="flex gap-5 group">
                  <div className={`shrink-0 w-12 h-12 flex items-center justify-center border rounded-xl transition-colors duration-200 ${
                    isDark
                      ? 'bg-[#14b8c8]/10 border-[#14b8c8]/20 group-hover:bg-[#14b8c8]/20'
                      : 'bg-[#0d7a8a]/10 border-[#0d7a8a]/20 group-hover:bg-[#0d7a8a]/20'
                  }`}>
                    <i className={`${b.icon} text-xl ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                  </div>
                  <div className={`flex-1 border-b pb-8 ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                    <h4 className={`font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>{b.title}</h4>
                    <p className={`text-sm leading-relaxed mb-3 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{b.description}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${b.metricIcon} text-sm ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                      </div>
                      <span className={`text-xs font-medium ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}>{b.metric}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}